import { useMemo, useCallback } from 'react';
import DeviceInfo from 'react-native-device-info';
import { useNavigation } from '@react-navigation/native';
import Config from 'react-native-config';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { IconProps } from '@ant-design/react-native';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useLegalContent } from './useLegalContent';
import { useSupportContent } from './useSupportContent';
import { useSubscriptionLink } from './useSubscriptionContent';
import { TUserStackParamList } from '../types';
import colorsConfig from '@shared/config/colorsConfig';
import { Linking } from 'react-native';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
type SettingsNavigationProp = NativeStackNavigationProp<TUserStackParamList>;

export interface IMenuItemConfig {
    icon: IconProps['name'];
    title: string;
    onPress: () => void;
    condition?: boolean;
    showArrow?: boolean;
    isLoading?: boolean;
    optionColor?: string;
    className?: string;
}

export interface IMenuSection {
    title?: string;
    items: IMenuItemConfig[];
}

export const useSettingsMenu = () => {
    const navigation = useNavigation<SettingsNavigationProp>();
    const { meData, logout } = useAuth();
    const { t } = useTranslation();
    const { isLoading, handleTerms, handlePrivacy } = useLegalContent();
    const { navigationHandlers: supportHandlers } = useSupportContent();

    const subscriptionId = meData?.subscription?.id;
    const { subscriptionLink, isLoading: isLoadingSubscription, refetch: refetchSubscriptionLink } = useSubscriptionLink({
        subscriptionId,
        enabled: false,
    });

    const handlePaymentDetails = useCallback(async () => {
        if (!subscriptionId) return;

        try {
            const result = await refetchSubscriptionLink();
            const link = result.data?.subscription_link;

            if (!link) {
                ToastNotifications.displayErrorNotification(
                    t('screens.profile.errors.subscription.invalidLink')
                );
                return;
            }

            await Linking.openURL(link);
        } catch (error) {
            ToastNotifications.displayErrorNotification(
                t('screens.profile.errors.subscription.cannotOpenLink')
            );
        }
    }, [subscriptionId, refetchSubscriptionLink, t]);

    const navigationHandlers = useMemo(() => ({
        handlePersonalData: () => navigation.navigate('PersonalData'),
        handleEmergencyContact: () => navigation.navigate('EmergencyContacts'),
        handlePathDetails: () => navigation.navigate('PathDetails'),
        handleMyPathway: () => navigation.navigate('MyPathway'),
        handleTeleassistanceRequest: () => navigation.navigate('TeleassistanceRequest'),
        handlePaymentDetails,
        handleDevPlayground: () => {
            navigation.navigate('DevPlayground');
        },
        handleLogout: () => {
            logout()
        }
    }), [navigation, handlePaymentDetails, logout]);

    const menuSections: IMenuSection[] = useMemo(() => [
        {
            items: [
                {
                    icon: 'user',
                    title: t('screens.profile.mainScreen.personalData'),
                    onPress: navigationHandlers.handlePersonalData,
                },
                {
                    icon: 'phone',
                    title: t('screens.profile.mainScreen.emergencyContact'),
                    onPress: navigationHandlers.handleEmergencyContact,
                },
            ],
        },
        {
            items: [
                {
                    icon: 'euro',
                    title: t('screens.profile.mainScreen.mySubscription'),
                    onPress: navigationHandlers.handlePaymentDetails,
                    condition: meData?.subscription?.id !== undefined,
                },
                {
                    icon: 'heart',
                    title: t('screens.profile.mainScreen.myPathway'),
                    onPress: navigationHandlers.handleMyPathway,
                    condition: meData?.organization?.payment_responsibility === 'patient',
                },
                {
                    icon: 'schedule',
                    title: t('screens.profile.support.requestTeleassistance'),
                    onPress: navigationHandlers.handleTeleassistanceRequest,
                },
            ],
        },
        {
            title: t('screens.profile.support.title'),
            items: [
                {
                    icon: 'question',
                    title: t('screens.profile.support.faq'),
                    onPress: supportHandlers.handleHelp,
                }
            ],
        },
        {
            title: t('screens.profile.legal.title'),
            items: [
                {
                    icon: 'file-text',
                    title: t('screens.profile.legal.terms'),
                    onPress: handleTerms,
                    showArrow: false,
                    isLoading: isLoading === 'terms_and_conditions',
                },
                {
                    icon: 'file-text',
                    title: t('screens.profile.legal.privacy'),
                    onPress: handlePrivacy,
                    showArrow: false,
                    isLoading: isLoading === 'privacy_policy',
                },
            ],
        },
        // Only for development and testing purposes
        {
            title: t('screens.profile.dev', 'Development'),
            items: [
                {
                    icon: 'alibaba',
                    title: t('screens.profile.devPlayground', 'Dev Playground'),
                    onPress: navigationHandlers.handleDevPlayground,
                    showArrow: false,
                    isLoading: isLoading === 'terms_and_conditions',
                    condition: meData?.organization.name.includes('DevTestOrg') || Config.APP_DEBUG_MODE === 'TRUE',

                }
            ],
        },
        {
            items: [
                {
                    icon: 'logout',
                    title: t('screens.profile.logout'),
                    onPress: navigationHandlers.handleLogout,
                    className: 'mt-4 ',
                    showArrow: false,
                    optionColor: colorsConfig.colorPalette.error[500],

                },
            ],
        },
        {
            items: [
                {
                    icon: 'appstore',
                    title: `v${DeviceInfo.getVersion()}`,
                    onPress: () => {},
                    className: 'opacity-40 text-xs',
                    showArrow: false,
                },
            ],
        },

    ], [navigationHandlers, meData, t, isLoading, handleTerms, handlePrivacy, supportHandlers]);

    return {
        menuSections,
        navigationHandlers,
    };
};
